

import type {
  BattleState,
  BattleActorState,
  BattleHeroSnapshot,
  BattleLogEntry,
  CardDef,
  CardVariant,
  CardEffect,
  EnemyTemplate,
  HitResult,
  ManaPool,
  Stats,
  StatusEffect,
  StatusEffectId,
} from "./types";
import { STATUS_DEFS } from "./types";
import { getCard } from "./cards";
import { ENEMY_CARDS } from "./enemies";
import { resolveHit, roll2d12, rollDice, defaultRng, type Rng } from "./dice";
import { statMod } from "./heroComputed";

const MAX_HOPE = 3;
const MAX_FEAR = 3;
const STARTING_HAND = 5;
const ENEMY_MAX_HOPE = 3;
const ENEMY_HOPE_REGEN = 3;


export function initBattle(
  hero: BattleHeroSnapshot,
  enemy: EnemyTemplate,
  rng: Rng = defaultRng,
): BattleState {
  const heroDeck = hero.deck.map(getCard);
  const enemyDeck = enemy.deck.map((id) => ENEMY_CARDS[id]).filter((c): c is CardDef => c != null);

  const heroActor: BattleActorState = {
    name: "Герой",
    hp: hero.hp,
    maxHp: hero.maxHp,
    armor: hero.armor,
    evasion: hero.evasion,
    mana: {
      hope: MAX_HOPE,
      maxHope: MAX_HOPE,
      fear: 1,
      maxFear: MAX_FEAR,
    },
    stats: hero.stats,
    keyStat: hero.keyStat,
    hitMod: hero.hitMod,
    critThreshold: hero.critThreshold,
    level: hero.level,
    statusEffects: [],
  };

  const enemyActor: BattleActorState = {
    name: enemy.name,
    hp: enemy.maxHp,
    maxHp: enemy.maxHp,
    armor: enemy.armor,
    evasion: enemy.evasion,
    mana: { hope: ENEMY_MAX_HOPE, maxHope: ENEMY_MAX_HOPE, fear: 0, maxFear: 0 },
    stats: { strength: 0, dexterity: 0, intelligence: 0, wisdom: 0, constitution: 0 },
    keyStat: "strength",
    hitMod: enemy.hitMod,
    critThreshold: enemy.critThreshold,
    level: enemy.level,
    statusEffects: [],
  };

  const state: BattleState = {
    turn: 1,
    activeSide: "hero",
    heroDomainId: hero.domainId,
    hero: heroActor,
    enemy: enemyActor,
    heroHand: [],
    heroDrawPile: shuffle(heroDeck, rng),
    heroDiscardPile: [],
    enemyDeck,
    enemyDeckIdx: 0,
    heroBonusNextAttack: 0,
    heroBonusAllAttacks: 0,
    heroFearAccumulated: 1,
    log: [{ turn: 0, side: "hero", text: `Бой с ${enemy.name} начался.` }],
  };

  return drawHand(state, STARTING_HAND);
}


function shuffle<T>(arr: T[], rng: Rng = defaultRng): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function log(state: BattleState, side: "hero" | "enemy", text: string): BattleState {
  const entry: BattleLogEntry = { turn: state.turn, side, text };
  return { ...state, log: [...state.log, entry].slice(-20) };
}


export function drawHand(state: BattleState, count: number, rng: Rng = defaultRng): BattleState {
  let hand = [...state.heroHand];
  let draw = [...state.heroDrawPile];
  let discard = [...state.heroDiscardPile];

  for (let i = 0; i < count; i++) {
    if (draw.length === 0) {
      if (discard.length === 0) break;
      draw = shuffle(discard, rng);
      discard = [];
    }
    const card = draw.pop();
    if (card) hand.push(card);
  }

  return { ...state, heroHand: hand, heroDrawPile: draw, heroDiscardPile: discard };
}


export interface ArmorResult {
  absorbed: number;
  dealt: number;
  newArmor: number;
}
export function applyArmor(incoming: number, armor: number, locked = false): ArmorResult {
  const absorbed = Math.min(incoming, armor);
  const dealt = Math.max(0, incoming - armor);
  const newArmor = locked ? armor : Math.max(0, armor - 1);
  return { absorbed, dealt, newArmor };
}


export interface EnemyModeChoice {
  mode: "hope" | "fear";
  crit: boolean;
  rolls: { hope: number; fear: number };
}
export function chooseEnemyMode(rng: Rng = defaultRng): EnemyModeChoice {
  const rolls = roll2d12(rng);
  if (rolls.hope === rolls.fear) {
    return { mode: "fear", crit: true, rolls };
  }
  return { mode: rolls.hope > rolls.fear ? "hope" : "fear", crit: false, rolls };
}



function statValue(actor: BattleActorState, stat: keyof Stats): number {
  const base = statMod(actor.stats[stat] ?? 0);
  const debuff = (actor.statusEffects ?? [])
    .filter((e) => e.id === "statDebuff" && e.stat === stat)
    .reduce((s, e) => s + (e.delta ?? 0) * e.stacks, 0);
  return base + debuff;
}


function computeBaseDamage(
  effect: Extract<CardEffect, { type: "damage" }>,
  source: BattleActorState,
  target: BattleActorState,
  rng: Rng,
): { dmg: number; diceDetail?: string; damageRoll?: DamageRollInfo; conditionMet: boolean } {
  let dmg = effect.base;
  let diceDetail: string | undefined;
  let damageRoll: DamageRollInfo | undefined;
  let conditionMet = false;

  let activeDice = effect.dice;
  if (effect.hpBelow) {
    const ref = effect.hpBelow.ofSelf ? source : target;
    const hpPct = (ref.hp / Math.max(1, ref.maxHp)) * 100;
    if (hpPct < effect.hpBelow.pct) {
      conditionMet = true;
      if (effect.hpBelow.replaceDice) activeDice = effect.hpBelow.replaceDice;
    }
  }

  if (activeDice) {
    const roll = rollDice(activeDice, rng);
    dmg += roll.sum;
    diceDetail = activeDice.count === 1
      ? `${activeDice.count}d${activeDice.sides}=${roll.sum}`
      : `${activeDice.count}d${activeDice.sides}=[${roll.rolls.join(",")}]=${roll.sum}`;
    damageRoll = { notation: activeDice, rolls: roll.rolls, sum: roll.sum };
  }

  if (effect.statScale) {
    dmg += statValue(source, effect.statScale.stat) * effect.statScale.mult;
  }
  if (effect.levelScale) {
    const effLevel = Math.max(1, source.level - totalStacks(source, "levelDown"));
    dmg += effLevel * effect.levelScale.mult;
  }
  dmg += totalStacks(source, "blessing");
  dmg -= totalStacks(source, "weak");
  return { dmg, diceDetail, damageRoll, conditionMet };
}


function computeBaseHeal(
  effect: Extract<CardEffect, { type: "heal" }>,
  source: BattleActorState,
  rng: Rng,
): number {
  let amount = effect.base;
  if (effect.dice) amount += rollDice(effect.dice, rng).sum;
  if (effect.statScale) amount += statValue(source, effect.statScale.stat) * effect.statScale.mult;
  if (effect.levelScale) amount += source.level * effect.levelScale.mult;
  return amount;
}



export interface DamageRollInfo {
  notation: { count: number; sides: number };
  rolls: number[];
  sum: number;
}

export interface CardApplyResult {
  state: BattleState;
  hitResult?: HitResult;
  damageDealt: number;
  healed: number;

  damageRolls: DamageRollInfo[];
}


function totalStacks(actor: BattleActorState, id: StatusEffectId): number {
  return (actor.statusEffects ?? [])
    .filter((e) => e.id === id)
    .reduce((s, e) => s + e.stacks, 0);
}

function hasUnbreakable(actor: BattleActorState): boolean {
  return (actor.statusEffects ?? []).some((e) => e.id === "unbreakable");
}

function hasAnyDebuff(actor: BattleActorState): boolean {
  return (actor.statusEffects ?? []).some(
    (e) => e.id === "poison" || e.id === "bleed" || e.id === "statDebuff",
  );
}

function addStatusEffect(state: BattleState, side: "hero" | "enemy", eff: StatusEffect): BattleState {
  const actor = side === "hero" ? state.hero : state.enemy;
  const list = [...(actor.statusEffects ?? [])];
  const same = list.find((e) => e.id === eff.id && e.dieSides === eff.dieSides);
  if (same) {
    same.stacks += eff.stacks;
    same.ttl = Math.max(same.ttl, eff.ttl);
  } else {
    list.push({ ...eff });
  }
  const next = { ...actor, statusEffects: list };
  return side === "hero" ? { ...state, hero: next } : { ...state, enemy: next };
}

function statusApplyLabel(effect: Extract<CardEffect, { type: "applyStatus" }>): string {
  const def = STATUS_DEFS[effect.status];
  const tgt = effect.target === "self" ? "себе" : "цели";
  const dur = effect.status === "stun" ? "" : (effect.ttl ? ` (${effect.ttl}х)` : " (до конца)");
  return `${def.label} ×${effect.stacks} — ${tgt}${dur}.`;
}

function tickStatusEffects(state: BattleState, side: "hero" | "enemy", rng: Rng): BattleState {
  const actor = side === "hero" ? state.hero : state.enemy;
  const list = actor.statusEffects ?? [];
  if (list.length === 0) return state;

  let s = state;
  let dotDmg = 0;
  let healAmt = 0;
  const breakdown: string[] = [];
  const remaining: StatusEffect[] = [];

  for (const eff of list) {
    if (eff.id === "poison") {
      const sides = eff.dieSides ?? 4;
      const roll = rollDice({ count: 1, sides }, rng);
      dotDmg += roll.sum;
      breakdown.push(`Яд(1d${sides}=${roll.sum})`);
    }
    if (eff.id === "bleed") {
      const sides = eff.dieSides ?? 4;
      const roll = rollDice({ count: 1, sides }, rng);
      const reduced = Math.max(0, roll.sum - actor.armor);
      dotDmg += reduced;
      breakdown.push(`Кровь(1d${sides}=${roll.sum}-броня${actor.armor}=${reduced})`);
    }
    if (eff.id === "regen") {
      healAmt += eff.stacks;
    }
    if (eff.id === "stun" || eff.id === "numbness") {
      if (eff.stacks > 1) remaining.push({ ...eff, stacks: eff.stacks - 1 });
      continue;
    }
    if (eff.ttl > 1) {
      remaining.push({ ...eff, ttl: eff.ttl - 1 });
    }
  }

  const next: BattleActorState = { ...actor, statusEffects: remaining };
  if (dotDmg > 0) next.hp = Math.max(0, next.hp - dotDmg);
  if (healAmt > 0) next.hp = Math.min(next.maxHp, next.hp + healAmt);
  s = side === "hero" ? { ...s, hero: next } : { ...s, enemy: next };

  if (dotDmg > 0) s = log(s, side, `Статус-урон: ${breakdown.join(" + ")} = ${dotDmg}. HP ${next.hp}.`);
  if (healAmt > 0) s = log(s, side, `Регенерация: +${healAmt}. HP ${next.hp}.`);
  return s;
}

function applyCardEffects(
  stateIn: BattleState,
  card: CardDef,
  variant: CardVariant,
  mode: "hope" | "fear",
  source: "hero" | "enemy",
  rng: Rng,

  enemyDoubleStrike: boolean = false,
  enemyPrecomputedRolls?: { a: number; b: number },
): CardApplyResult {
  let state = stateIn;
  const sourceActor = source === "hero" ? state.hero : state.enemy;
  const oppositeSide = source === "hero" ? "enemy" : "hero";
  let damageDealt = 0;
  let healed = 0;
  let hitResult: HitResult | undefined;
  const damageRolls: DamageRollInfo[] = [];

  const enhancementEffects = source === "hero" ? card.enhancements[state.heroDomainId]?.[mode] ?? [] : [];
  const effects = enhancementEffects.length > 0 ? [...variant.effects, ...enhancementEffects] : variant.effects;

  const hitBoost = totalStacks(sourceActor, "hitBoost");
  const needsHitRoll = effects.some((e) =>
    e.type === "damage" ||
    (e.type === "applyStatus" && e.target === "enemy") ||
    (e.type === "armorDebuff" && e.value > 0),
  );
  if (needsHitRoll) {
    const targetActor = source === "hero" ? state.enemy : state.hero;
    if (source === "enemy" && enemyDoubleStrike) {
      const a = enemyPrecomputedRolls?.a ?? 0;
      const b = enemyPrecomputedRolls?.b ?? 0;
      hitResult = {
        source: "enemy",
        roll: { raw: a + b, modifier: sourceActor.hitMod + hitBoost, total: (a + b) + sourceActor.hitMod + hitBoost },
        targetEvasion: targetActor.evasion,
        hit: true,
        crit: true,
        enemyRolls: enemyPrecomputedRolls ? { a, b } : undefined,
      };
    } else {
      hitResult = resolveHit(sourceActor.hitMod + hitBoost, Math.max(0, targetActor.evasion - totalStacks(targetActor, "chill")), rng, source, sourceActor.critThreshold ?? 20, enemyPrecomputedRolls);
    }
  }

  const missed = needsHitRoll && hitResult !== undefined && !hitResult.hit;
  if (missed && hitResult) {
    state = degradeArmor(state, oppositeSide);
    const rollDesc = hitResult.enemyRolls
      ? `2d12: ${hitResult.enemyRolls.a}+${hitResult.enemyRolls.b}+${hitResult.roll.modifier}=${hitResult.roll.total}`
      : `d20: ${hitResult.roll.raw}+${hitResult.roll.modifier}=${hitResult.roll.total}`;
    state = log(state, source, `Промах (${rollDesc} < ${hitResult.targetEvasion}) — эффекты по цели не сработали.`);
  }

  for (const effect of effects) {
    switch (effect.type) {
      case "damage": {
        if (!hitResult?.hit) break;
        const targetActor = source === "hero" ? state.enemy : state.hero;
        const { dmg: baseDmg, diceDetail, damageRoll, conditionMet } = computeBaseDamage(effect, sourceActor, targetActor, rng);
        if (damageRoll) damageRolls.push(damageRoll);
        let dmg = baseDmg;
        const parts: string[] = [];
        if (effect.base) parts.push(`база ${effect.base}`);
        if (diceDetail) parts.push(conditionMet ? `${diceDetail} [усл]` : diceDetail);
        if (effect.statScale) {
          const sv = statValue(sourceActor, effect.statScale.stat);
          const total = sv * effect.statScale.mult;
          const statLabel = ({ strength: "СИЛ", dexterity: "ЛОВ", intelligence: "ИНТ", wisdom: "МУД", constitution: "ТЕЛ" } as Record<string, string>)[effect.statScale.stat] ?? effect.statScale.stat;
          parts.push(effect.statScale.mult === 1
            ? `${statLabel}(+${sv})`
            : `${effect.statScale.mult}×${statLabel}(+${total})`);
        }
        if (effect.levelScale) {
          const lvl = Math.max(1, sourceActor.level - totalStacks(sourceActor, "levelDown"));
          const total = lvl * effect.levelScale.mult;
          parts.push(effect.levelScale.mult === 1
            ? `Ур(+${lvl})`
            : `${effect.levelScale.mult}×Ур(+${total})`);
        }
        const blessingBonus = totalStacks(sourceActor, "blessing");
        if (blessingBonus > 0) parts.push(`Благ(+${blessingBonus})`);
        const weakPenalty = totalStacks(sourceActor, "weak");
        if (weakPenalty > 0) parts.push(`Слаб(-${weakPenalty})`);

        for (const buf of (sourceActor.statusEffects ?? [])) {
          if (buf.id !== "damageDie") continue;
          const sides = buf.dieSides ?? 4;
          for (let k = 0; k < buf.stacks; k++) {
            const r = rollDice({ count: 1, sides }, rng);
            dmg += r.sum;
            parts.push(`буст(1d${sides}=${r.sum})`);
          }
        }

        if (effect.bonusIfTargetHasDebuff && hasAnyDebuff(targetActor)) {
          const d = effect.bonusIfTargetHasDebuff.dice;
          const r = rollDice(d, rng);
          dmg += r.sum;
          parts.push(`скрытно(${d.count}d${d.sides}=${r.sum})`);
        }

        let bonusApplied = 0;
        if (source === "hero") {
          if (state.heroBonusAllAttacks > 0) {
            dmg += state.heroBonusAllAttacks;
            bonusApplied += state.heroBonusAllAttacks;
          }
          if (state.heroBonusNextAttack > 0) {
            dmg += state.heroBonusNextAttack;
            bonusApplied += state.heroBonusNextAttack;
            state = { ...state, heroBonusNextAttack: 0 };
          }
        }
        if (bonusApplied > 0) parts.push(`бонус(+${bonusApplied})`);

        const targetActorForArmor = oppositeSide === "hero" ? state.hero : state.enemy;
        const targetArmor = targetActorForArmor.armor;
        const ar = applyArmor(dmg, targetArmor, hasUnbreakable(targetActorForArmor));
        let finalDmg = ar.dealt;
        const armorPart = targetArmor > 0 ? ` − армор(${targetArmor}) = ${finalDmg}` : "";

        const critMult = hitResult.crit ? 2 : 1;
        if (hitResult.crit) finalDmg *= 2;

        damageDealt += finalDmg;
        state = applyDamageToSide(state, oppositeSide, finalDmg, ar.newArmor);

        const critLabel = hitResult.crit ? (enemyDoubleStrike ? " ДУБЛЬ-КРИТ!" : " КРИТ!") : "";
        const rollLabel = hitResult.enemyRolls
          ? `2d12 ${hitResult.enemyRolls.a}+${hitResult.enemyRolls.b}+${hitResult.roll.modifier}=${hitResult.roll.total}`
          : (source === "enemy" ? "дубль 2d12" : `d20 ${hitResult.roll.total}`);
        const breakdown = parts.length > 0 ? ` [${parts.join(" + ")} = ${dmg}${armorPart}${critMult > 1 ? ` × ${critMult} крит` : ""}]` : "";
        state = log(state, source, `Урон ${finalDmg}${critLabel}${breakdown} (${rollLabel} vs Укл ${hitResult.targetEvasion}).`);
        if (ar.newArmor < targetArmor) {
          state = log(state, source, `Армор противника −${targetArmor - ar.newArmor} (от удара).`);
        }
        break;
      }

      case "heal": {
        const amount = computeBaseHeal(effect, sourceActor, rng);
        healed += amount;
        state = healSide(state, source, amount);
        state = log(state, source, `+${amount} HP.`);
        break;
      }

      case "draw": {
        if (source === "hero") {
          state = drawHand(state, effect.count, rng);
          state = log(state, source, `+${effect.count} карт.`);
        }
        break;
      }

      case "bonusAttack": {
        if (source === "hero") {
          const bonus = statValue(state.hero, effect.bonusFromStat);
          if (effect.scope === "nextAttack") {
            state = { ...state, heroBonusNextAttack: state.heroBonusNextAttack + bonus };
          } else {
            state = { ...state, heroBonusAllAttacks: state.heroBonusAllAttacks + bonus };
          }
          state = log(state, source, `Бонус к атаке: +${bonus}.`);
        }
        break;
      }

      case "armorDebuff": {
        if (effect.onlyOnCrit && !hitResult?.crit) break;
        if (missed && effect.value > 0) break;
        if (effect.value > 0) {
          const targetActor = oppositeSide === "hero" ? state.hero : state.enemy;
          if (hasUnbreakable(targetActor)) {
            state = log(state, source, `Несокрушимость поглотила −${effect.value} армора.`);
          } else {
            state = modifyArmor(state, oppositeSide, -effect.value);
            state = log(state, source, `Армор противника −${effect.value} (пробитие).`);
          }
        } else {
          state = modifyArmor(state, source, -effect.value);
          state = log(state, source, `+${-effect.value} армора.`);
        }
        break;
      }

      case "bonusDraw": {
        if (effect.onlyOnCrit && !hitResult?.crit) break;
        if (source === "hero") {
          state = drawHand(state, effect.count, rng);
          state = log(state, source, `+${effect.count} карт (крит).`);
        }
        break;
      }

      case "applyStatus": {
        if (missed && (effect.onlyOnHit || effect.target === "enemy")) break;
        const side = effect.target === "self" ? source : oppositeSide;
        state = addStatusEffect(state, side, {
          id: effect.status,
          stacks: effect.stacks,
          ttl: effect.ttl ?? 99,
          dieSides: effect.dieSides,
          stat: effect.stat,
          delta: effect.delta,
        });
        state = log(state, source, statusApplyLabel(effect));
        break;
      }

      case "armorBuff": {
        let bonus = effect.base;
        if (effect.dice) bonus += rollDice(effect.dice, rng).sum;
        if (effect.statScale) bonus += statValue(sourceActor, effect.statScale.stat) * effect.statScale.mult;
        state = modifyArmor(state, source, bonus);
        state = log(state, source, `+${bonus} армора.`);
        break;
      }

      case "selfDamage": {
        let dmg = effect.base;
        if (effect.dice) dmg += rollDice(effect.dice, rng).sum;
        if (effect.statScale) dmg += statValue(sourceActor, effect.statScale.stat) * effect.statScale.mult;
        const beforeHp = source === "hero" ? state.hero.hp : state.enemy.hp;
        const afterHp = Math.max(0, beforeHp - dmg);
        state = source === "hero"
          ? { ...state, hero: { ...state.hero, hp: afterHp } }
          : { ...state, enemy: { ...state.enemy, hp: afterHp } };
        state = log(state, source, `Самоурон ${dmg}. HP ${afterHp}.`);
        break;
      }
    }
  }

  if (source === "enemy") {
    state = applyFearGain(state, mode, variant.cost);
  }

  return { state, hitResult, damageDealt, healed, damageRolls };
}


function applyFearGain(state: BattleState, mode: "hope" | "fear", amount: number): BattleState {
  if (mode !== "fear" || amount <= 0) return state;
  return {
    ...state,
    heroFearAccumulated: Math.min(state.heroFearAccumulated + amount, MAX_FEAR),
    hero: {
      ...state.hero,
      mana: {
        ...state.hero.mana,
        fear: Math.min(state.hero.mana.fear + amount, state.hero.mana.maxFear),
      },
    },
  };
}

function degradeArmor(state: BattleState, side: "hero" | "enemy"): BattleState {
  const actor = side === "hero" ? state.hero : state.enemy;
  if (hasUnbreakable(actor)) return state;
  const newArmor = Math.max(0, actor.armor - 1);
  return side === "hero"
    ? { ...state, hero: { ...state.hero, armor: newArmor } }
    : { ...state, enemy: { ...state.enemy, armor: newArmor } };
}

function applyDamageToSide(state: BattleState, side: "hero" | "enemy", dmg: number, newArmor: number): BattleState {
  if (side === "hero") {
    return {
      ...state,
      hero: { ...state.hero, hp: Math.max(0, state.hero.hp - dmg), armor: newArmor },
    };
  } else {
    return {
      ...state,
      enemy: { ...state.enemy, hp: Math.max(0, state.enemy.hp - dmg), armor: newArmor },
    };
  }
}

function healSide(state: BattleState, side: "hero" | "enemy", amount: number): BattleState {
  if (side === "hero") {
    return { ...state, hero: { ...state.hero, hp: Math.min(state.hero.maxHp, state.hero.hp + amount) } };
  }
  return { ...state, enemy: { ...state.enemy, hp: Math.min(state.enemy.maxHp, state.enemy.hp + amount) } };
}

function modifyArmor(state: BattleState, side: "hero" | "enemy", delta: number): BattleState {
  if (side === "hero") {
    return { ...state, hero: { ...state.hero, armor: Math.max(0, state.hero.armor + delta) } };
  }
  return { ...state, enemy: { ...state.enemy, armor: Math.max(0, state.enemy.armor + delta) } };
}



export function canPlayHeroCard(state: BattleState, handIndex: number, mode: "hope" | "fear"): boolean {
  if (state.activeSide !== "hero") return false;
  const card = state.heroHand[handIndex];
  if (!card) return false;
  const variant = mode === "hope" ? card.hope : card.fear;
  if (mode === "hope") return state.hero.mana.hope >= variant.cost;
  return state.hero.mana.hope >= variant.cost && state.hero.mana.fear >= variant.cost;
}


export function consumeHeroCard(state: BattleState, handIndex: number, mode: "hope" | "fear"): BattleState {
  const card = state.heroHand[handIndex];
  if (!card) return state;
  const variant = mode === "hope" ? card.hope : card.fear;
  const mana: ManaPool = { ...state.hero.mana };
  mana.hope -= variant.cost;
  if (mode === "fear") mana.fear -= variant.cost;
  return {
    ...state,
    hero: { ...state.hero, mana },
    heroHand: state.heroHand.filter((_, i) => i !== handIndex),
    heroDiscardPile: [...state.heroDiscardPile, card],
  };
}

export function playHeroCard(
  state: BattleState,
  handIndex: number,
  mode: "hope" | "fear",
  rng: Rng = defaultRng,
): CardApplyResult {
  if (!canPlayHeroCard(state, handIndex, mode)) {
    return { state, damageDealt: 0, healed: 0, damageRolls: [] };
  }
  const card = state.heroHand[handIndex];
  const variant = mode === "hope" ? card.hope : card.fear;

  const mana: ManaPool = { ...state.hero.mana };
  mana.hope -= variant.cost;
  if (mode === "fear") mana.fear -= variant.cost;

  let nextState: BattleState = {
    ...state,
    hero: { ...state.hero, mana },
    heroHand: state.heroHand.filter((_, i) => i !== handIndex),
    heroDiscardPile: [...state.heroDiscardPile, card],
  };

  nextState = log(nextState, "hero", `Разыграна «${card.name}» (${mode === "hope" ? "Надежда" : "Страх"}).`);

  return applyCardEffects(nextState, card, variant, mode, "hero", rng);
}


export function endHeroTurn(state: BattleState, rng: Rng = defaultRng): BattleState {
  const debuff = totalStacks(state.enemy, "stun") + totalStacks(state.enemy, "numbness");
  const regen = Math.max(0, ENEMY_HOPE_REGEN - debuff);
  const newHope = Math.min(state.enemy.mana.maxHope, state.enemy.mana.hope + regen);
  let s: BattleState = {
    ...state,
    activeSide: "enemy",
    heroBonusNextAttack: 0,
    heroBonusAllAttacks: 0,
    enemy: {
      ...state.enemy,
      mana: { ...state.enemy.mana, hope: newHope },
    },
  };
  s = tickStatusEffects(s, "enemy", rng);
  return s;
}


export interface EnemyAction {
  card: CardDef;
  mode: "hope" | "fear";
  modeCrit: boolean;
  diceRolls: { hope: number; fear: number };
  hitResult?: HitResult;
  damageDealt: number;
  healed: number;
  damageRolls: DamageRollInfo[];
  stateAfter: BattleState;
}

export interface EnemyTurnResult {
  state: BattleState;
  actions: EnemyAction[];
}

export function enemyTurn(state: BattleState, rng: Rng = defaultRng): EnemyTurnResult {
  const actions: EnemyAction[] = [];
  let s = state;

  for (let safety = 0; safety < 12; safety++) {
    if (isGameOver(s)) break;
    const hope = s.enemy.mana.hope;
    if (hope <= 0) break;

    let foundIdx = -1;
    for (let k = 0; k < s.enemyDeck.length; k++) {
      const idx = (s.enemyDeckIdx + k) % s.enemyDeck.length;
      const c = s.enemyDeck[idx];
      const minCost = Math.min(c.hope.cost, c.fear.cost);
      if (minCost <= hope) { foundIdx = idx; break; }
    }
    if (foundIdx === -1) break;

    const card = s.enemyDeck[foundIdx];
    const choice = chooseEnemyMode(rng);

    let mode: "hope" | "fear" = choice.mode;
    const chosenCost = mode === "hope" ? card.hope.cost : card.fear.cost;
    if (chosenCost > hope) mode = mode === "hope" ? "fear" : "hope";
    const variant = mode === "hope" ? card.hope : card.fear;

    s = {
      ...s,
      enemy: { ...s.enemy, mana: { ...s.enemy.mana, hope: hope - variant.cost } },
      enemyDeckIdx: (foundIdx + 1) % s.enemyDeck.length,
    };

    s = log(
      s,
      "enemy",
      `${s.enemy.name} играет «${card.name}» (${mode === "hope" ? "Надежда" : "Страх"}${choice.crit ? " КРИТ" : ""}, −${variant.cost} нaд). [2d12: ${choice.rolls.hope}/${choice.rolls.fear}]`,
    );

    const enemyRolls = { a: choice.rolls.hope, b: choice.rolls.fear };
    const result = applyCardEffects(s, card, variant, mode, "enemy", rng, choice.crit, enemyRolls);
    s = result.state;

    actions.push({
      card,
      mode,
      modeCrit: choice.crit,
      diceRolls: choice.rolls,
      hitResult: result.hitResult,
      damageDealt: result.damageDealt,
      healed: result.healed,
      damageRolls: result.damageRolls,
      stateAfter: s,
    });
  }

  return { state: s, actions };
}


export function startHeroTurn(state: BattleState, rng: Rng = defaultRng): BattleState {
  const debuff = totalStacks(state.hero, "stun") + totalStacks(state.hero, "numbness");
  const regen = Math.max(0, state.hero.mana.maxHope - debuff);
  let s1: BattleState = {
    ...state,
    activeSide: "hero",
    turn: state.turn + 1,
    hero: {
      ...state.hero,
      mana: { ...state.hero.mana, hope: Math.min(state.hero.mana.maxHope, regen) },
    },
  };
  s1 = tickStatusEffects(s1, "hero", rng);
  return drawHand(s1, 1, rng);
}


export interface InitiativeResult {
  heroRoll: number;
  enemyRoll: number;
  heroGoesFirst: boolean;
}


export function rollInitiative(heroDex: number, enemyDex: number, rng: Rng = defaultRng): InitiativeResult {
  const heroRoll = Math.floor(rng() * 20) + 1 + heroDex;
  const enemyRoll = Math.floor(rng() * 20) + 1 + enemyDex;
  return { heroRoll, enemyRoll, heroGoesFirst: heroRoll >= enemyRoll };
}


export type BattleOutcome = "hero_won" | "enemy_won" | null;

export function isGameOver(state: BattleState): BattleOutcome {
  if (state.enemy.hp <= 0) return "hero_won";
  if (state.hero.hp <= 0) return "enemy_won";
  return null;
}

export { roll2d12 } from "./dice";
